/**
 * Llamadas HTTP del dominio Órdenes.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials:
 * 'include'`, el `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request,
 * así que estos listados/acciones operan sobre la sede activa sin tocar las
 * cabeceras a mano. Un `ApiError` (401/403/404/4xx/5xx) lo traduce el cliente y
 * lo gestionan los hooks/pantallas.
 *
 * Cubre la superficie de la #47 (listado paginado + filtros, detalle y las
 * transiciones de estado) y la #48 (alta `POST` y edición parcial `PATCH`).
 */
import { api } from "@/lib/api";

import type {
  OrderAction,
  OrderDetail,
  OrdersPage,
  OrdersQuery,
  OrderWriteBody,
} from "./types";

/** Construye el query string de `GET /orders` desde los filtros. */
function buildOrdersSearchParams(query: OrdersQuery): URLSearchParams {
  return new URLSearchParams({
    status: query.status,
    search: query.search,
    page: String(query.page),
    per_page: String(query.per_page),
  });
}

/**
 * Listado paginado de órdenes (`GET /api/v1/orders`).
 *
 * Permiso backend: `view_orders`. Devuelve la página con `items` en vista lista
 * + metadatos de paginación (`page`, `per_page`, `total`, `pages`).
 */
export async function fetchOrders(query: OrdersQuery): Promise<OrdersPage> {
  const response = await api.get("orders", {
    searchParams: buildOrdersSearchParams(query),
  });
  return (await response.json()) as OrdersPage;
}

/**
 * Detalle de una orden (`GET /api/v1/orders/<id>`).
 *
 * Permiso backend: `view_orders`. Devuelve la vista detalle (superset de la
 * lista: montos desglosados, descripción y timestamps).
 */
export async function fetchOrder(id: number): Promise<OrderDetail> {
  const response = await api.get(`orders/${id}`);
  return (await response.json()) as OrderDetail;
}

/** Ruta (relativa al prefijo) de cada transición sobre una orden. */
const ACTION_PATH: Record<Exclude<OrderAction, "delete">, string> = {
  complete: "complete",
  deliver: "deliver",
  cancel: "cancel",
  reactivate: "reactivate",
};

/**
 * Ejecuta una transición de estado sobre una orden.
 *
 *  - `complete|deliver|cancel|reactivate`: `POST /orders/<id>/<accion>` (200 con
 *    la orden actualizada en vista detalle).
 *  - `delete`: `DELETE /orders/<id>` (204 sin cuerpo) -> devuelve `null`.
 *
 * Los permisos los exige el backend; la UI replica el gating antes de llamar.
 * Devuelve la orden actualizada (o `null` en delete) para que el llamador pueda
 * refrescar el detalle si lo tiene abierto.
 */
export async function transitionOrder(
  id: number,
  action: OrderAction,
): Promise<OrderDetail | null> {
  if (action === "delete") {
    await api.delete(`orders/${id}`);
    return null;
  }
  const response = await api.post(`orders/${id}/${ACTION_PATH[action]}`);
  return (await response.json()) as OrderDetail;
}

/**
 * Crea una orden (`POST /api/v1/orders`).
 *
 * Permiso backend: `add_orders`. El cuerpo es JSON con los campos del contrato
 * (`progress/explore_orders_api.md`, §2.3); las fechas deben ir en `DD/MM/YYYY`
 * (lo prepara el formulario). El backend orquesta talonario/auditoría y devuelve
 * la orden creada en vista detalle (201). Los errores de validación
 * (`{error_code, message}`) los traduce `ApiError` y los muestra el formulario.
 */
export async function createOrder(body: OrderWriteBody): Promise<OrderDetail> {
  const response = await api.post("orders", { json: body });
  return (await response.json()) as OrderDetail;
}

/**
 * Edita una orden (`PATCH /api/v1/orders/<id>`), semántica parcial.
 *
 * Permiso backend: `edit_orders` + permisos granulares por campo. El formulario
 * envía SOLO los campos que el usuario puede editar (los demás ni se incluyen),
 * por lo que `body` es un subconjunto de `OrderWriteBody`. Devuelve la orden
 * actualizada en vista detalle (200).
 */
export async function updateOrder(
  id: number,
  body: Partial<OrderWriteBody>,
): Promise<OrderDetail> {
  const response = await api.patch(`orders/${id}`, { json: body });
  return (await response.json()) as OrderDetail;
}
